
import React from 'react';
import { User, ArenaConfig, UserRole } from '../../types';
import { Trophy, Settings, ListOrdered, Grid, LogOut, Calendar, History } from 'lucide-react';

// --- Color sanitizer: prevents CSS injection via theme values ---
const sanitizeColor = (color: string | undefined, fallback: string): string => {
  if (!color) return fallback;
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color.trim())) return color.trim();
  return fallback;
};

interface LayoutProps {
  config: ArenaConfig;
  currentUser: User | null;
  isAdmin: boolean;
  isLoggedIn: boolean;
  adminView: string;
  setAdminView: (view: any) => void;
  adminNotificationCount: number;
  onLogout: () => void;
  onShowSettings: () => void;
  children: React.ReactNode;
}

export const StandardLayout: React.FC<LayoutProps> = ({
  config,
  currentUser,
  isAdmin,
  isLoggedIn,
  adminView,
  setAdminView,
  adminNotificationCount,
  onLogout,
  onShowSettings,
  children
}) => {
  const isDarkMode = config.theme?.darkMode;
  const primaryColor = sanitizeColor(config.theme?.primaryColor, '#10b981');
  const secondaryColor = sanitizeColor(config.theme?.secondaryColor, '#064e3b');

  const calendarConfirmedSafe = sanitizeColor(config.theme?.calendarConfirmedColor, secondaryColor);
  const calendarPendingSafe = sanitizeColor(config.theme?.calendarPendingColor, '#f97316');

  const styleVars = `
    :root {
      --color-primary: ${primaryColor};
      --color-secondary: ${secondaryColor};
      --color-primary-light: ${primaryColor}20;
      --color-calendar-confirmed: ${calendarConfirmedSafe};
      --color-calendar-pending: ${calendarPendingSafe};
    }
    
    body {
      background-color: ${isLoggedIn ? (isDarkMode ? '#0f172a' : '#f8fafc') : '#020617'};
      color: ${isLoggedIn ? (isDarkMode ? '#e2e8f0' : '#0f172a') : '#ffffff'};
      transition: background-color 0.2s ease;
      font-family: 'Inter', sans-serif !important;
    }

    ${isDarkMode ? `
      header { background-color: #111827 !important; border-bottom-color: #1f2937 !important; color: #f1f5f9 !important; }
      .bg-white { background-color: #1f2937 !important; color: #f1f5f9 !important; border-color: #374151 !important; }
    ` : ''}
    
    .theme-text-primary { color: var(--color-primary); }
    .theme-bg-primary { background-color: var(--color-primary); }
    .theme-bg-secondary { background-color: var(--color-secondary); }
    .theme-border-primary { border-color: var(--color-primary); }

    .admin-nav-item.active {
      background-color: ${isDarkMode ? 'var(--color-primary)' : '#ffffff'} !important;
      color: ${isDarkMode ? '#ffffff' : 'var(--color-primary)'} !important;
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
    }

    .bg-slate-950 .flex.flex-col.gap-4.text-center > .flex.items-center.gap-4.my-4,
    .bg-slate-950 .flex.flex-col.gap-4.text-center > div.flex.gap-3 {
      display: none !important;
    }

    /* Footer text color override for 'still' in standard layout */
    footer span[style*="color: #0f172a"],
    footer span[style*="color: rgb(15, 23, 42)"] {
      color: #ffffff !important;
    }
  `;

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col font-sans bg-[#020617]">
        <style>{styleVars}</style>
        {children}
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col font-sans`}>
      <style>{styleVars}</style>
      
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex flex-wrap md:flex-nowrap items-center justify-between sticky top-0 z-[80] shadow-sm animate-fade-in-down gap-y-2">
        <div className="flex items-center gap-3 order-1">
          {config.logoUrl ? (
            <img src={config.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-xl" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white theme-bg-primary shadow-lg shadow-emerald-500/20 rotate-3 transition-transform hover:rotate-0">
                <Trophy size={20} />
            </div>
          )}
          <div>
            <h1 className="font-black text-sm md:text-base text-slate-800 tracking-tighter uppercase italic">{config.name}</h1>
            {isAdmin && <span className="text-[10px] font-black theme-text-primary uppercase tracking-widest leading-none block mt-0.5">Control Center</span>}
          </div>
        </div>

        {isAdmin && (
          <div className={`flex order-3 md:order-2 w-full md:w-auto justify-center mt-2 md:mt-0 p-1 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-slate-100'}`}>
            <button
              onClick={() => setAdminView('EDITOR')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${
                adminView === 'EDITOR' ? 'active' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <Grid size={14} /> <span className="hidden lg:inline">Überblick</span>
            </button>
            <button
              onClick={() => setAdminView('RESERVATIONS')}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${
                adminView === 'RESERVATIONS' ? 'active' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <ListOrdered size={14} /> <span className="hidden lg:inline">Buchungen</span>
              {adminNotificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse flex items-center justify-center text-[8px] text-white font-bold">{adminNotificationCount}</span>
              )}
            </button>
            <button
              onClick={() => setAdminView('CALENDAR')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${
                adminView === 'CALENDAR' ? 'active' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <Calendar size={14} /> <span className="hidden lg:inline">Kalender</span>
            </button>
            <button
              onClick={() => setAdminView('HISTORY')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${
                adminView === 'HISTORY' ? 'active' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <History size={14} /> <span className="hidden lg:inline">Historie</span>
            </button>
          </div>
        )}

        <div className="flex gap-2 md:gap-4 items-center order-2 md:order-3 ml-auto md:ml-0">
          {isAdmin && (
            <button 
              onClick={onShowSettings}
              className="flex items-center justify-center w-10 h-10 rounded-xl text-slate-500 hover:bg-slate-100 transition-all border border-slate-200"
              title="Einstellungen"
            >
              <Settings size={20} /> 
            </button>
          )}

          <div className="flex items-center gap-4 pl-4 border-l border-slate-200">
            <div className="text-right hidden sm:block">
                <div className="text-xs font-black text-slate-800 uppercase tracking-tight">
                  {currentUser ? currentUser.name : 'Gast-Besucher'}
                </div>
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                  {isAdmin ? 'Administrator' : currentUser ? 'Arena Member' : 'Einfache Buchung'}
                </div>
            </div>
            <button 
                onClick={onLogout}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg shadow-black/10 ${isDarkMode ? 'bg-gray-800 text-red-400 hover:bg-gray-700' : 'theme-bg-secondary text-white hover:opacity-90'}`}
            >
                <LogOut size={16} /> <span className="hidden md:inline">{currentUser ? 'Logout' : 'Beenden'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 relative ${adminView === 'EDITOR' && isAdmin ? 'overflow-hidden' : 'overflow-auto'} ${isDarkMode ? 'bg-gray-950 p-4 md:p-10' : 'bg-slate-50 p-4 md:p-10'}`}>
        {children}
      </main>
    </div>
  );
};
